import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useMemo } from 'react';
import { InlineNotification, ModalWindow, Text, useLocalizeMessage } from 'libs.nucleus';
import { StepIndicator } from 'libs.react';
import { useChartsWithNewerVersion, useDashboards } from '../../hooks';
import { CreateDashboardForm } from '../create_dashboard_modal/create_dashboard_form.component';
import { SelectCharts } from '../create_dashboard_modal/select_charts.component';
export const EditDashboardModal = ({ isOpen, onClose, onSave, dashboard, chartRefs }) => {
    const translate = useLocalizeMessage();
    const { updateDashboardDetails, isLoading } = useDashboards();
    // SSP-3756 alignment with chart-builder Edit details modal: surface a
    // top-of-modal banner when at least one chart in this dashboard has a
    // newer version available, so the user editing the dashboard is aware of
    // stale picks before saving.
    const { chartsWithNewerVersions } = useChartsWithNewerVersion(chartRefs);
    // Resolve the affected chart-ref ids back to display names so the banner
    // lists each chart that needs updating (mirrors the chart-builder banner
    // which names the specific dataset).
    const affectedChartNames = useMemo(() => chartRefs
        .filter((ref) => chartsWithNewerVersions.has(ref.libraryResourceId || ref.id))
        .map((ref) => ref.title || ref.name)
        .filter((name) => Boolean(name)), [chartRefs, chartsWithNewerVersions]);
    const hasChartsWithNewerVersions = affectedChartNames.length > 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dashboardData = dashboard.data;
    const initialDatasetId = dashboard.cubeConfigId || dashboardData?.cubeConfigId || '';
    const initialDatasetKey = dashboard.cubeConfigKey || dashboardData?.cubeConfigKey || '';
    // Step navigation (2 steps: details + charts)
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 2;
    const isFirstStep = currentStep === 1;
    const isLastStep = currentStep === totalSteps;
    // Step 1: Details + Dataset
    const [formName, setFormName] = useState(dashboard.name);
    const [formDescription, setFormDescription] = useState(dashboard.data?.description || '');
    const [formDatasetId, setFormDatasetId] = useState(initialDatasetId);
    const [formDatasetKey, setFormDatasetKey] = useState(initialDatasetKey);
    const [isFormValid, setIsFormValid] = useState(true);
    // Step 2: Chart selection — initialised from existing dashboard chart refs
    const [selectedCharts, setSelectedCharts] = useState(() => chartRefs.map((ref) => ({
        resourceId: ref.libraryResourceId || ref.id,
        resourceKey: ref.libraryResourceKey || ref.id,
        name: ref.name,
        type: ref.type,
        title: ref.title,
    })));
    const existingChartIds = useMemo(() => chartRefs.map((ref) => ref.libraryResourceId || ref.id), [chartRefs]);
    const handleFormChange = useCallback((name, description) => {
        setFormName(name);
        setFormDescription(description);
    }, []);
    const handleDatasetChange = useCallback((datasetId, datasetKey) => {
        setFormDatasetId(datasetId);
        setFormDatasetKey(datasetKey);
        // Reset chart selection when dataset changes
        setSelectedCharts([]);
    }, []);
    const handleValidationChange = useCallback((valid) => {
        setIsFormValid(valid);
    }, []);
    const handleNext = useCallback(() => {
        if (currentStep < totalSteps) {
            setCurrentStep(currentStep + 1);
        }
    }, [currentStep]);
    const handlePrevious = useCallback(() => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    }, [currentStep]);
    const handleCloseModal = useCallback(() => {
        setCurrentStep(1);
        onClose();
    }, [onClose]);
    // Build DashboardChartRef[] from the current selection, preserving existing refs where possible.
    // New charts get grid coords stacked below existing tiles when the dashboard already uses persisted grid layout.
    const buildUpdatedChartRefs = useCallback(() => {
        const defaultYSpan = 2;
        const hasAnyGridCoords = chartRefs.some((r) => typeof r.gridX === 'number' && typeof r.gridY === 'number');
        let stackY = hasAnyGridCoords
            ? chartRefs.reduce((max, ref) => {
                if (typeof ref.gridY !== 'number') {
                    return max;
                }
                return Math.max(max, ref.gridY + (ref.ySpan ?? defaultYSpan));
            }, 0)
            : -1;
        return selectedCharts.map((chart, index) => {
            const existing = chartRefs.find((ref) => (ref.libraryResourceId || ref.id) === chart.resourceId);
            if (existing) {
                return { ...existing, position: index + 1 };
            }
            if (stackY >= 0) {
                const newRef = {
                    id: chart.resourceKey,
                    libraryResourceId: chart.resourceId,
                    libraryResourceKey: chart.resourceKey,
                    name: chart.name,
                    title: chart.title,
                    type: chart.type,
                    position: index + 1,
                    gridX: 0,
                    gridY: stackY,
                    xSpan: 12,
                    ySpan: defaultYSpan,
                };
                stackY += defaultYSpan;
                return newRef;
            }
            return {
                id: chart.resourceKey,
                libraryResourceId: chart.resourceId,
                libraryResourceKey: chart.resourceKey,
                name: chart.name,
                title: chart.title,
                type: chart.type,
                position: index + 1,
            };
        });
    }, [selectedCharts, chartRefs]);
    const handleSave = useCallback(async () => {
        try {
            const updatedCharts = buildUpdatedChartRefs();
            const updated = await updateDashboardDetails(dashboard.id, {
                name: formName.trim(),
                description: formDescription.trim() || undefined,
                cubeConfigId: formDatasetId,
                cubeConfigKey: formDatasetKey,
                charts: updatedCharts,
            });
            onSave(updated, updatedCharts);
            handleCloseModal();
        }
        catch {
            // Error handled in hook
        }
    }, [
        updateDashboardDetails,
        dashboard.id,
        formName,
        formDescription,
        formDatasetId,
        formDatasetKey,
        buildUpdatedChartRefs,
        onSave,
        handleCloseModal,
    ]);
    // Footer buttons — mirrors create modal pattern
    const continueButton = {
        label: translate('Continue'),
        onClick: handleNext,
        disabled: !isFormValid,
    };
    const saveButton = {
        label: translate('Save'),
        onClick: handleSave,
        disabled: isLoading || selectedCharts.length === 0,
    };
    const footerSecondaryActionButtons = isFirstStep
        ? [
            {
                label: translate('Cancel'),
                onClick: handleCloseModal,
            },
        ]
        : [
            {
                label: translate('Cancel'),
                onClick: handleCloseModal,
            },
            {
                label: translate('Back'),
                onClick: handlePrevious,
            },
        ];
    const stepFooter = useMemo(() => (_jsxs("div", { className: 'flex justify-center items-center', children: [_jsx(Text, { size: 'sm', className: 'px-6', children: translate('Step {current} of {total}', {
                    current: currentStep,
                    total: totalSteps,
                }) }), _jsx(StepIndicator, { currentStep: currentStep, totalSteps: totalSteps })] })), [currentStep, translate]);
    if (!isOpen) {
        return null;
    }
    return (_jsx(ModalWindow, { title: translate('Edit dashboard'), isOpen: true, closeWindow: handleCloseModal, footerPrimaryActionButton: isLastStep ? saveButton : continueButton, footerSecondaryActionButtons: footerSecondaryActionButtons, footerCenterContent: stepFooter, width: 'full', children: _jsxs("div", { className: 'min-h-[400px] size-full flex flex-col gap-4', children: [hasChartsWithNewerVersions && (_jsx(InlineNotification, { type: 'info', title: translate('Newer chart versions available'), subtitle: _jsxs("div", { className: 'flex flex-col gap-2', children: [_jsx(Text, { size: 'sm', children: translate('These charts in the dashboard have a newer version in the Library. After saving, use Review versions on the dashboard page to apply the update.') }), _jsx("ul", { className: 'list-disc pl-6 text-sm', children: affectedChartNames.map((name) => (_jsx("li", { children: _jsx(Text, { size: 'sm', children: name }) }, name))) })] }) })), currentStep === 1 && (_jsx(CreateDashboardForm, { name: formName, description: formDescription, selectedDatasetId: formDatasetId, onFormChange: handleFormChange, onDatasetChange: handleDatasetChange, onValidationChange: handleValidationChange, excludeId: dashboard.id })), currentStep === 2 && (_jsx(SelectCharts, { cubeConfigKey: formDatasetKey, selectedCharts: selectedCharts, onSelectionChange: setSelectedCharts, existingChartIds: existingChartIds }))] }) }));
};
